import { useAuth } from '@/hooks/useAuth';

export type Permission =
  | 'task.create'
  | 'task.read'
  | 'task.update'
  | 'task.delete'
  | 'project.create'
  | 'project.read'
  | 'project.update'
  | 'project.delete'
  | 'user.create'
  | 'user.read'
  | 'user.update'
  | 'user.delete'
  | 'user.manage'
  | 'organization.manage'
  | 'team.manage'
  | 'report.view'
  | 'settings.manage';

export type Role = 'user' | 'admin' | 'manager';

// Map backend role slugs/names to frontend role types
const ROLE_SLUG_MAP: Record<string, Role> = {
  // Super Admin variants
  'super-admin': 'admin',
  'super admin': 'admin',
  'superadmin': 'admin',

  // Admin variants
  'admin': 'admin',
  'administrator': 'admin',
  'administrador': 'admin',

  // Manager variants
  'manager': 'manager',
  'project-manager': 'manager',
  'project manager': 'manager',
  'gestor': 'manager',
  'gestor de projetos': 'manager',

  // Member/User variants
  'member': 'user',
  'membro': 'user',
  'user': 'user',
  'viewer': 'user',
  'visualizador': 'user',
};

// Define permissions for each role
const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  admin: [
    'task.create', 'task.read', 'task.update', 'task.delete',
    'project.create', 'project.read', 'project.update', 'project.delete',
    'user.create', 'user.read', 'user.update', 'user.delete', 'user.manage',
    'organization.manage', 'team.manage',
    'report.view', 'settings.manage',
  ],
  manager: [
    'task.create', 'task.read', 'task.update', 'task.delete',
    'project.create', 'project.read', 'project.update', 'project.delete',
    'user.read',
    'organization.manage', 'team.manage',
    'report.view',
  ],
  user: [
    'task.read', 'task.update',
    'project.read',
    'user.read',
  ],
};

/**
 * Normalize role from backend to frontend role type
 * Checks both user.role and user.primaryRole.slug
 */
function normalizeRole(user: any): Role {
  if (!user) return 'user';

  // Check primaryRole.slug first (most reliable)
  const primaryRoleSlug = user.primaryRole?.slug?.toLowerCase();
  if (primaryRoleSlug && ROLE_SLUG_MAP[primaryRoleSlug]) {
    return ROLE_SLUG_MAP[primaryRoleSlug];
  }

  // Check user.role (can be slug or name)
  const roleString = user.role?.toLowerCase();
  if (roleString && ROLE_SLUG_MAP[roleString]) {
    return ROLE_SLUG_MAP[roleString];
  }

  // Check user.roles array (legacy support)
  if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
    const firstRole = user.roles[0]?.toLowerCase();
    if (firstRole && ROLE_SLUG_MAP[firstRole]) {
      return ROLE_SLUG_MAP[firstRole];
    }
  }

  // Default to user
  console.warn('⚠️ Could not determine user role, defaulting to "user"', {
    role: user.role,
    primaryRole: user.primaryRole,
    roles: user.roles
  });
  return 'user';
}

export function usePermissions() {
  const { user, isAuthenticated } = useAuth();

  const userRole = normalizeRole(user);

  const hasPermission = (permission: Permission): boolean => {
    if (!isAuthenticated || !user) return false;

    // Admin has all permissions
    if (userRole === 'admin') return true;

    return ROLE_PERMISSIONS[userRole]?.includes(permission) ?? false;
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  const isAdmin = (): boolean => userRole === 'admin';

  const isManager = (): boolean => userRole === 'manager' || userRole === 'admin';

  const isUser = (): boolean => userRole === 'user';

  const canManageUsers = (): boolean => hasPermission('user.manage');

  const canManageProjects = (): boolean =>
    hasPermission('project.create') && hasPermission('project.update');

  const canDeleteTasks = (): boolean => hasPermission('task.delete');

  const canDeleteProjects = (): boolean => hasPermission('project.delete');

  const canViewReports = (): boolean => hasPermission('report.view');

  const canManageSettings = (): boolean => hasPermission('settings.manage');

  // Check if user can perform action on a resource
  const canEditTask = (task: { createdBy?: string; assignedTo?: string }): boolean => {
    if (isAdmin() || isManager()) return true;
    if (!user) return false;
    return task.createdBy === user.id || task.assignedTo === user.id;
  };

  const canDeleteTask = (task: { createdBy?: string }): boolean => {
    if (isAdmin()) return true;
    if (!user) return false;
    return task.createdBy === user.id;
  };

  const canEditProject = (project: { ownerId?: string }): boolean => {
    if (isAdmin() || isManager()) return true;
    if (!user) return false;
    return project.ownerId === user.id;
  };

  const canDeleteProject = (project: { ownerId?: string }): boolean => {
    if (isAdmin()) return true;
    if (!user) return false;
    return project.ownerId === user.id;
  };

  const canChangeTaskStatus = (task: { assignedTo?: string }): boolean => {
    if (!user) return false;
    return task.assignedTo === user.id;
  };

  return {
    userRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isManager,
    isUser,
    canManageUsers,
    canManageProjects,
    canDeleteTasks,
    canDeleteProjects,
    canViewReports,
    canManageSettings,
    canEditTask,
    canDeleteTask,
    canEditProject,
    canDeleteProject,
    canChangeTaskStatus,
  };
}
